import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectUIVersion } from "store/app";
import { Box, Button as ThemeUiButton } from "theme-ui";
import debounce from "lodash/debounce";

import { useMeasure } from "hooks";
import { ArrowLeftIcon, ArrowRightIcon } from "components/icons";
import * as styles from "./HorizontalScrollView.styles";

function LeftButton({ onClick, disabled, UIVersion }) {
  return (
    <ThemeUiButton
      variant="unstyled"
      sx={styles.leftButton({ UIVersion })}
      onClick={onClick}
      disabled={disabled}
    >
      <ArrowLeftIcon sx={styles.arrowIcons({ UIVersion })} />
    </ThemeUiButton>
  );
}

function RightButton({ onClick, disabled, UIVersion }) {
  return (
    <ThemeUiButton
      variant="unstyled"
      sx={styles.rightButton({ UIVersion })}
      onClick={onClick}
      disabled={disabled}
    >
      <ArrowRightIcon sx={styles.arrowIcons({ UIVersion })} />
    </ThemeUiButton>
  );
}

export default function HorizontalScrollView({
  itemGap = 0,
  sideGutter = 0,
  height = 0,
  sideButtonWidth = 0,
  leftButtonComponent,
  rightButtonComponent,
  justifyContent = "center",
  children,

  scrollPositions = {},
  scrollPositionId,
  onScroll = () => {},
  ...props
}) {
  const UIVersion = useSelector(selectUIVersion);

  const parentLocalRef = useRef();

  const [scrollPosition, setScrollPosition] = useState(0);

  const handleVerticalScrollToHorizontal = (ev) => {
    parentLocalRef.current.scrollLeft +=
      Math.abs(ev.deltaY) > Math.abs(ev.deltaX) ? ev.deltaY : ev.deltaX;
  };

  useEffect(() => {
    parentLocalRef.current.scrollTo({
      top: 0,
      left: scrollPositions[scrollPositionId] ?? 0,
      behavior: "auto",
    });
  }, [scrollPositionId]);

  useEffect(() => {
    parentLocalRef.current.scrollTo({
      top: 0,
      left: scrollPosition,
      behavior: "smooth",
    });
  }, [scrollPosition]);

  useEffect(() => {
    parentLocalRef.current.addEventListener(
      "wheel",
      handleVerticalScrollToHorizontal,
      { passive: true }
    );

    return () => {
      if (parentLocalRef?.current) {
        parentLocalRef.current.removeEventListener(
          "wheel",
          handleVerticalScrollToHorizontal
        );
      }
    };
  }, []);

  const useMeasureOptions = { debounce: 300 };
  const [parentRef, { width: parentWidth }] = useMeasure(useMeasureOptions);
  const [childLocalRef, { width: childWidth }] = useMeasure(useMeasureOptions);

  const maxScrollPosition = Math.floor(childWidth - parentWidth);

  const isReady = parentWidth > 0 && childWidth > 0;

  const shouldShowSideButtons = isReady && childWidth > parentWidth;

  const handleLeftButtonClick = () => {
    setScrollPosition(Math.max(0, scrollPosition - 200));
  };

  const handleRightButtonClick = () => {
    setScrollPosition(Math.min(maxScrollPosition, scrollPosition + 200));
  };

  const handleScroll = (evt) => {
    setScrollPosition(evt.target.scrollLeft);
    onScroll(evt.target.scrollLeft);
  };

  const leftArrowButtonProps = {
    onClick: handleLeftButtonClick,
    disabled: scrollPosition <= 0,
    UIVersion,
  };

  const rightArrowButtonProps = {
    onClick: handleRightButtonClick,
    disabled: scrollPosition >= maxScrollPosition,
    UIVersion,
  };

  return (
    <Box
      sx={styles.horizontalScrollViewContainer({
        isReady,
      })}
      ref={parentRef}
    >
      <Box sx={styles.buttonContainer({ shouldShowSideButtons, side: "left" })}>
        {(shouldShowSideButtons || justifyContent === "center") && (
          <>
            {leftButtonComponent ? (
              leftButtonComponent(leftArrowButtonProps)
            ) : (
              <LeftButton {...leftArrowButtonProps} />
            )}
          </>
        )}
      </Box>

      <Box
        sx={styles.horizontalScrollView({ height, justifyContent })}
        {...props}
      >
        <Box
          sx={styles.itemsContainer}
          ref={parentLocalRef}
          onScroll={debounce(handleScroll, 300)}
        >
          <Box ref={childLocalRef} sx={{ width: "max-content" }}>
            <Box
              sx={styles.items({
                itemGap,
                height,
                sideGutter,
                sideButtonWidth,
              })}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={styles.buttonContainer({ shouldShowSideButtons, side: "right" })}
      >
        {(shouldShowSideButtons || justifyContent === "center") && (
          <>
            {rightButtonComponent ? (
              rightButtonComponent(rightArrowButtonProps)
            ) : (
              <RightButton {...rightArrowButtonProps} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
